import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    vendasMock,
    solucoesCatalogoMock,
} from '../mocks/nogleTechData';
import api from '../services/api';
import { useToast } from '@chakra-ui/react';
import { useAuth } from '../hooks/auth';

const NogleTechContext = createContext(null);

const resolveLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    // Base URL encontrada no sistema legado (Contel ERP)
    return `https://erp.nogle.tech/img/logo-marcas/${logo}`;
};

const toBool = (v) => {
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
        const s = v.trim().toUpperCase();
        return s === 'SIM' || s === 'S' || s === '1' || s === 'TRUE';
    }
    return false;
};

export const NogleTechProvider = ({ children }) => {
    const { user } = useAuth();
    const [marcas, setMarcas] = useState([]);
    const [vendas, setVendas] = useState(vendasMock);
    const [solucoes, setSolucoes] = useState(solucoesCatalogoMock);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const [statusMsg, setStatusMsg] = useState('Conectando ao banco de dados...');
    const toast = useToast();

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [0, 2000, 4000]; // backoff exponencial

    const fetchMarcas = async (attempt = 0) => {
        try {
            setLoading(true);
            setErro(null);
            setRetryCount(attempt);

            if (attempt === 0) {
                setStatusMsg('Conectando ao banco de dados...');
            } else {
                setStatusMsg(`Tentativa ${attempt + 1} de ${MAX_RETRIES}...`);
                setIsRetrying(true);
            }

            const { data } = await api.get('/comercial/marcas');
            const processed = (Array.isArray(data) ? data : []).map(m => ({
                ...m,
                logo: resolveLogoUrl(m.logo),
                logo_login: resolveLogoUrl(m.logo_login)
            }));
            setMarcas(processed);
            setErro(null);
            setStatusMsg('');
            setIsRetrying(false);
        } catch (error) {
            console.error(`Erro ao buscar marcas (tentativa ${attempt + 1}):`, error);

            if (attempt + 1 < MAX_RETRIES) {
                const delay = RETRY_DELAYS[attempt + 1] || 4000;
                setStatusMsg(`Falha na conexão. Tentando novamente em ${delay / 1000}s...`);
                await new Promise(resolve => setTimeout(resolve, delay));
                return fetchMarcas(attempt + 1);
            }

            // Todas as tentativas falharam
            setErro('Não foi possível carregar as marcas do banco de dados.');
            setIsRetrying(false);
            setStatusMsg('');
            toast({
                title: 'Erro de conexão',
                description: 'Não foi possível conectar ao servidor após múltiplas tentativas.',
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
        } finally {
            if (retryCount >= MAX_RETRIES - 1 || !isRetrying) {
                setLoading(false);
            }
        }
    };

    const refetchMarcas = () => {
        setErro(null);
        setIsRetrying(false);
        fetchMarcas(0);
    };

    useEffect(() => {
        if (user) {
            fetchMarcas(0);
        } else {
            setMarcas([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Persistência local para "Edições" sem tocar no banco SQL
    const [edicoesLocais, setEdicoesLocais] = useState(() => {
        const saved = localStorage.getItem('edicoesMarcas');
        return saved ? JSON.parse(saved) : {};
    });

    const atualizarMarca = async (id, patch) => {
        try {
            // Campos que o usuário editou manualmente
            const technicalFields = ['site', 'sac', 'facebook', 'instagram', 'whatsapp', 'url_app_google', 'url_app_apple', 'url_painel_cliente', 'url_vendas_direta', 'provedor', 'responsavel_marca_nome', 'responsavel_retencao_nome', 'observacoes'];
            
            const hasTechnicalFields = Object.keys(patch).some(k => technicalFields.includes(k));

            if (hasTechnicalFields) {
                const novasEdicoes = {
                    ...edicoesLocais,
                    [id]: { ...(edicoesLocais[id] || {}), ...patch }
                };
                setEdicoesLocais(novasEdicoes);
                localStorage.setItem('edicoesMarcas', JSON.stringify(novasEdicoes));
            }

            setMarcas((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
            
            if (hasTechnicalFields) {
                toast({
                    title: 'Ajuste Local',
                    description: 'Informação salva no seu navegador (sem alterar o banco).',
                    status: 'info',
                    duration: 2000,
                });
            }
            return true;
        } catch (error) {
            console.error('Erro ao atualizar marca localmente:', error);
            return false;
        }
    };

    const moverFluxo = (id, fluxo) => atualizarMarca(id, { fluxo });

    const buscarDetalhe = async (id) => {
        const { data } = await api.get(`/comercial/marcas/${id}`);
        
        const processed = {
            ...data,
            logo: resolveLogoUrl(data.logo),
            logo_login: resolveLogoUrl(data.logo_login)
        };

        // Mesclar com edições locais antes de retornar
        if (edicoesLocais[id]) {
            return { ...processed, ...edicoesLocais[id] };
        }
        return processed;
    };

    const adicionarVenda = (venda) =>
        setVendas((prev) => [{ id: Date.now(), ...venda }, ...prev]);

    const adicionarMarca = (marca) =>
        setMarcas((prev) => [...prev, { id: Date.now(), ...marca }]);

    const adicionarSolucao = (sol) => setSolucoes((prev) => [...prev, sol]);

    const atualizarSolucao = (marca, patch) =>
        setSolucoes((prev) =>
            prev.map((s) => (s.marca === marca ? { ...s, ...patch } : s))
        );

    const indicadores = useMemo(() => {
        const total = marcas.length;
        const comApp = marcas.filter((m) => toBool(m.status_possui_aplicativo)).length;
        const comProvedor = marcas.filter((m) => !!m.provedor).length;
        const entreguesContel = marcas.filter((m) =>
            toBool(m.site_feito_contel) ||
            toBool(m.app_google_feito_contel) ||
            toBool(m.app_apple_feito_contel) ||
            toBool(m.painel_feito_contel) ||
            toBool(m.venda_direta_feito_contel)
        ).length;
        const provedores = marcas.reduce((acc, m) => {
            const p = (m.provedor || '').trim();
            if (!p) return acc;
            acc[p] = (acc[p] || 0) + 1;
            return acc;
        }, {});
        return { total, comApp, comProvedor, entreguesContel, provedores };
    }, [marcas]);

    const alertas = useMemo(() => [], []);

    const marcasFinal = useMemo(() => {
        return marcas.map((m) => {
            if (edicoesLocais[m.id]) {
                return { ...m, ...edicoesLocais[m.id] };
            }
            return m;
        });
    }, [marcas, edicoesLocais]);

    const value = {
        marcas: marcasFinal,
        vendas,
        solucoes,
        loading,
        erro,
        statusMsg,
        retryCount,
        isRetrying,
        refetchMarcas,
        indicadores,
        alertas,
        buscarDetalhe,
        adicionarVenda,
        adicionarMarca,
        atualizarMarca,
        moverFluxo,
        adicionarSolucao,
        atualizarSolucao,
        toBool,
    };

    return (
        <NogleTechContext.Provider value={value}>
            {children}
        </NogleTechContext.Provider>
    );
};

export const useNogleTech = () => {
    const ctx = useContext(NogleTechContext);
    if (!ctx) throw new Error('useNogleTech deve estar dentro de NogleTechProvider');
    return ctx;
};
